import React from 'react';

type IntegrationCategory = 'pos' | 'delivery' | 'middleware' | 'reservations' | 'labour' | 'inventory' | 'reviews' | 'accounting' | 'marketing';

interface IntegrationsViewProps {
    category: IntegrationCategory;
    onIntegrate: (app: any, category: string) => void;
    onViewData?: (appName: string) => void;
    connectedApps: string[];
}

export function IntegrationsView({ category, onIntegrate, onViewData, connectedApps }: IntegrationsViewProps) {
    type AppAccess = 'self_serve' | 'partner_only';
    type App = { name: string; status: string; desc: string; access: AppAccess };

    const getContent = () => {
        switch (category) {
            case 'pos':
                return {
                    title: "POS / EPOS",
                    subtitle: "Point of Sale / Electronic Point of Sale",
                    icon: "💳",
                    apps: [
                        // Self-serve: Square has a public developer platform + OAuth (no partnership required).
                        { name: "Square", status: connectedApps.includes("Square") ? "Connected" : "Available", desc: "Integrated payment & POS", access: "self_serve" },

                        // Partner-only / gated: keep in code but hidden from the catalog for now.
                        { name: "Lightspeed", status: connectedApps.includes("Lightspeed") ? "Connected" : "Available", desc: "Cloud-based POS system", access: "partner_only" },
                        { name: "Toast", status: connectedApps.includes("Toast") ? "Connected" : "Available", desc: "Restaurant management & POS", access: "partner_only" },
                        { name: "Zettle", status: connectedApps.includes("Zettle") ? "Connected" : "Available", desc: "Card readers & POS", access: "partner_only" }
                    ]
                };
            case 'delivery':
                return {
                    title: "Delivery Platforms",
                    subtitle: "Direct marketplace integrations like Uber Eats, Deliveroo",
                    icon: "🛵",
                    apps: [
                        { name: "Uber Eats", status: connectedApps.includes("Uber Eats") ? "Connected" : "Available", desc: "Global food delivery", access: "partner_only" },
                        { name: "Deliveroo", status: connectedApps.includes("Deliveroo") ? "Connected" : "Available", desc: "Direct marketplace integration", access: "partner_only" },
                        { name: "Just Eat", status: connectedApps.includes("Just Eat") ? "Connected" : "Available", desc: "Local food delivery marketplace", access: "partner_only" }
                    ]
                };
            case 'middleware':
                return {
                    title: "Middleware",
                    subtitle: "Aggregators like Deliverect, Otter",
                    icon: "🔌",
                    apps: [
                        { name: "Deliverect", status: connectedApps.includes("Deliverect") ? "Connected" : "Available", desc: "Menu & order management", access: "partner_only" },
                        { name: "Otter", status: connectedApps.includes("Otter") ? "Connected" : "Available", desc: "Restaurant aggregator & insights", access: "partner_only" }
                    ]
                };
            case 'reservations':
                return {
                    title: "Reservations",
                    subtitle: "Table booking and guest CRM",
                    icon: "📅",
                    apps: [
                        { name: "Findsy", status: connectedApps.includes("Findsy") ? "Connected" : "Available", desc: "Native table booking and guest CRM integration", access: "self_serve" },

                        // Partner-only / gated
                        { name: "OpenTable", status: connectedApps.includes("OpenTable") ? "Connected" : "Available", desc: "Guest management & booking", access: "partner_only" },
                        { name: "SevenRooms", status: connectedApps.includes("SevenRooms") ? "Connected" : "Available", desc: "Guest engagement & CRM", access: "partner_only" },
                        { name: "Resy", status: connectedApps.includes("Resy") ? "Connected" : "Available", desc: "Reservation & waitlist", access: "partner_only" },

                        // Future partner integrations (hidden until partnership is in place)
                        { name: "DesignMyNight", status: connectedApps.includes("DesignMyNight") ? "Connected" : "Available", desc: "Bookings & guestlist platform", access: "partner_only" },
                    ]
                };
            case 'labour':
                return {
                    title: "Labour",
                    subtitle: "Workforce management and rostering",
                    icon: "👥",
                    apps: [
                        { name: "7shifts", status: connectedApps.includes("7shifts") ? "Connected" : "Available", desc: "Team management & scheduling", access: "self_serve" },
                        { name: "Deputy", status: connectedApps.includes("Deputy") ? "Connected" : "Available", desc: "Workforce management", access: "self_serve" },

                        // Partner-only / gated
                        { name: "Planday", status: connectedApps.includes("Planday") ? "Connected" : "Available", desc: "Employee scheduling & payroll", access: "partner_only" },
                    ]
                };
            case 'inventory':
                return {
                    title: "Inventory",
                    subtitle: "Stock control and purchasing",
                    icon: "📦",
                    apps: [
                        { name: "MarketMan", status: connectedApps.includes("MarketMan") ? "Connected" : "Available", desc: "Inventory & supply chain", access: "partner_only" },
                        { name: "Rekki", status: connectedApps.includes("Rekki") ? "Connected" : "Available", desc: "Ordering & supplier management", access: "partner_only" },
                        { name: "Fourth", status: connectedApps.includes("Fourth") ? "Connected" : "Available", desc: "Inventory & labor management", access: "partner_only" },

                        // Future partner integrations (hidden until partnership is in place)
                        { name: "Tevalis", status: connectedApps.includes("Tevalis") ? "Connected" : "Available", desc: "POS and back-office suite", access: "partner_only" }
                    ]
                };
            case 'reviews':
                return {
                    title: "Reviews",
                    subtitle: "Review platforms like Google, TripAdvisor",
                    icon: "⭐",
                    apps: [
                        { name: "Google Reviews", status: connectedApps.includes("Google Reviews") ? "Connected" : "Available", desc: "Sentiment & review monitoring", access: "self_serve" },
                        { name: "Yelp", status: connectedApps.includes("Yelp") ? "Connected" : "Available", desc: "Local business reviews", access: "self_serve" },

                        // Partner-only / gated
                        { name: "TripAdvisor", status: connectedApps.includes("TripAdvisor") ? "Connected" : "Available", desc: "Reputation management", access: "partner_only" },
                    ]
                };
            case 'accounting':
                return {
                    title: "Accounting",
                    subtitle: "Financial software",
                    icon: "💰",
                    apps: [
                        { name: "Xero", status: connectedApps.includes("Xero") ? "Connected" : "Available", desc: "Cloud accounting for business", access: "self_serve" },
                        { name: "QuickBooks", status: connectedApps.includes("QuickBooks") ? "Connected" : "Available", desc: "Small business financial software", access: "self_serve" },

                        // Partner-only / gated
                        { name: "Sage", status: connectedApps.includes("Sage") ? "Connected" : "Available", desc: "Business accounting & HR", access: "partner_only" },
                    ]
                };
            case 'marketing':
                return {
                    title: "Marketing",
                    subtitle: "Email marketing and ad platforms",
                    icon: "📢",
                    apps: [
                        { name: "Mailchimp", status: connectedApps.includes("Mailchimp") ? "Connected" : "Available", desc: "Email marketing & automation", access: "self_serve" },
                        { name: "Meta Ads", status: connectedApps.includes("Meta Ads") ? "Connected" : "Available", desc: "Facebook & Instagram ads", access: "self_serve" },
                        { name: "Google Ads", status: connectedApps.includes("Google Ads") ? "Connected" : "Available", desc: "Search engine marketing", access: "self_serve" },
                    ]
                };
            default:
                return { title: "", subtitle: "", icon: "", apps: [] };
        }
    };

    const rawData = getContent();
    const data = {
        ...rawData,
        // Hide partner-only integrations from the catalog until partnerships are in place.
        apps: (rawData.apps as App[]).filter(a => a.access !== 'partner_only')
    };

    return React.createElement("div", { className: "space-y-8 animate-in fade-in duration-500 pb-12" }, [
        // Header
        React.createElement("div", { key: "header", className: "bg-white p-8 rounded-3xl border border-gray-200 shadow-sm flex items-center gap-6" }, [
            React.createElement("div", { className: "w-16 h-16 bg-indigo-50 rounded-2xl flex items-center justify-center text-4xl shadow-inner border border-indigo-100/50" }, data.icon),
            React.createElement("div", {}, [
                React.createElement("h2", { className: "text-3xl font-black text-gray-900" }, data.title),
                React.createElement("p", { className: "text-gray-500 font-medium" }, data.subtitle)
            ])
        ]),

        // Apps Grid
        data.apps.length > 0
            ? React.createElement("div", { key: "apps", className: "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" },
                data.apps.map(app =>
                    React.createElement("div", { key: app.name, className: "bg-white p-6 rounded-3xl border border-gray-200 shadow-sm hover:shadow-md transition-all group" }, [
                        React.createElement("div", { className: "flex justify-between items-start mb-6" }, [
                            React.createElement("div", { className: "w-12 h-12 bg-gray-50 rounded-xl flex items-center justify-center text-xl font-black text-gray-400 group-hover:bg-indigo-50 group-hover:text-indigo-600 transition-colors overflow-hidden" },
                                app.name === "Findsy"
                                    ? React.createElement("img", { src: "/logo.png", className: "w-8 h-auto object-contain" })
                                    : app.name.charAt(0)
                            ),
                            React.createElement("span", {
                                className: `text-[10px] px-2 py-1 rounded-full font-black uppercase tracking-tighter ${app.status === 'Connected' ? 'bg-green-50 text-green-600' : 'bg-gray-100 text-gray-500'}`
                            }, app.status)
                        ]),
                        React.createElement("h3", { className: "font-black text-gray-900 mb-1" }, app.name),
                        React.createElement("p", { className: "text-xs text-gray-500 mb-6 font-medium" }, app.desc),
                        app.status === 'Connected'
                            ? React.createElement("button", {
                                className: "w-full py-2 bg-green-50 text-green-700 text-[10px] font-black uppercase rounded-xl border border-green-200 hover:bg-green-100 transition-all",
                                onClick: () => onViewData && onViewData(app.name)
                            }, "View Shared Data")
                            : React.createElement("button", {
                                className: "w-full py-2 bg-indigo-600 text-white text-[10px] font-black uppercase rounded-xl hover:bg-indigo-700 transition-all shadow-md shadow-indigo-100",
                                onClick: () => onIntegrate(app, category)
                            }, "Integrate Now")
                    ])
                )
            )
            : React.createElement("div", { key: "apps-empty", className: "bg-white rounded-3xl border border-gray-200 shadow-sm p-8 text-center" }, [
                React.createElement("p", { key: "t", className: "text-sm font-black text-gray-900" }, "No self-serve integrations in this category yet."),
                React.createElement("p", { key: "s", className: "text-xs text-gray-500 font-medium mt-2 max-w-md mx-auto" }, "Some providers require a formal partnership or commercial agreement. Once you have access, we can enable them here.")
            ]),

        // Need Help Section
        React.createElement("div", { key: "help", className: "max-w-2xl mx-auto w-full" },
            React.createElement("div", { className: "bg-indigo-900 rounded-3xl p-10 text-white text-center flex flex-col items-center shadow-xl" }, [
                React.createElement("div", { className: "text-4xl mb-4" }, "🛠️"),
                React.createElement("h3", { className: "text-2xl font-black mb-2" }, "Can't find your stack?"),
                React.createElement("p", { className: "text-indigo-200 max-w-sm mb-8 font-medium" }, "Our engineers can build custom API bridges for enterprise partners. Reach out to discuss your requirements."),
                React.createElement("button", { className: "px-8 py-3 bg-white text-indigo-900 font-black rounded-xl hover:bg-indigo-50 transition-all text-sm shadow-xl" }, "Request Custom Integration")
            ])
        )
    ]);
}
